import { RoomData } from '@/components/SpacePlanningPanel/SpacePlanningTypes';

export interface BioScoreBase {
	materials: number; // 0–10
	views: number; // 0–10
	daylight: number; // 0–10
}

export interface BioScoreBonus {
	waterVisibility?: boolean;
	waterAudibility?: boolean;
	acousticTreatment?: boolean;
}

export interface BioScoreNoise {
	noise: number; // 0-10
}

export interface BioScoreResult {
	bonusScore: number;
	rawFinalScore: number;
	displayedScore: number;
}

export interface BonusContribution {
	label: string;
	weight: number;
	enabled: boolean;
	weighted: number;
}

export const BONUS_WEIGHTS = {
	waterVisibility: 1,
	waterAudibility: 2,
	acousticTreatment: 1,
} as const;

export type BioScoreColorLevel = 'high' | 'medium' | 'low';

export function getBioScoreColorLevel(score: number): BioScoreColorLevel {
	if (score >= 8) return 'high';
	if (score >= 5) return 'medium';
	return 'low';
}

export const BIO_SCORE_GRADIENTS: Record<BioScoreColorLevel, string> = {
	// TODO: see with the team which colors are better
	// high: 'linear-gradient(135deg, #00C851, #007E33)',
	// medium: 'linear-gradient(135deg, #FFBB33, #FF8800)',
	// low: 'linear-gradient(135deg, #FF4444, #CC0000)',

	high: 'linear-gradient(135deg, #6FBF9B, #4E9F7D)',
	medium: 'linear-gradient(135deg, #E6C07B, #D6A84F)',
	low: 'linear-gradient(135deg, #D17A7A, #B85C5C)',
};

export function calculateBioScoreNoise({ noise }: BioScoreNoise): number {
	return noise * 0.3;
}

export function calculateBioScoreBonus({
	waterVisibility,
	waterAudibility,
	acousticTreatment,
}: BioScoreBonus): number {
	let bonus = 0;

	if (waterVisibility) bonus += 1;
	if (waterAudibility) bonus += 2;
	if (acousticTreatment) bonus += 1;

	return bonus;
}

export function getRoomBonusFlags(roomData: RoomData): Required<BioScoreBonus> {
	return {
		waterVisibility: Number(roomData.waterFeatures?.visibility ?? 0) > 0,
		waterAudibility: Number(roomData.waterFeatures?.audibility ?? 0) > 0,
		acousticTreatment: Number(roomData.acoustics?.planned ?? 0) > 0,
	};
}

export function getBonusContributions(flags: Required<BioScoreBonus>): BonusContribution[] {
	return [
		{
			label: 'Water Visibility',
			weight: BONUS_WEIGHTS.waterVisibility,
			enabled: flags.waterVisibility,
			weighted: flags.waterVisibility ? BONUS_WEIGHTS.waterVisibility : 0,
		},
		{
			label: 'Water Audibility',
			weight: BONUS_WEIGHTS.waterAudibility,
			enabled: flags.waterAudibility,
			weighted: flags.waterAudibility ? BONUS_WEIGHTS.waterAudibility : 0,
		},
		{
			label: 'Acoustic Treatment',
			weight: BONUS_WEIGHTS.acousticTreatment,
			enabled: flags.acousticTreatment,
			weighted: flags.acousticTreatment ? BONUS_WEIGHTS.acousticTreatment : 0,
		},
	];
}

export function calculateBioScoreBase({ materials, views, daylight }: BioScoreBase): number {
	return materials * 0.3 + views * 0.3 + daylight * 0.4;
}

export function calculateFinalBioScoreForRoom(roomData: RoomData): BioScoreResult {
	const baseScore = calculateBioScoreBase({
		materials: roomData.scoreBreakdown?.materials ?? 0,
		daylight: roomData.scoreBreakdown?.sunlight ?? 0,
		views: roomData.scoreBreakdown?.view ?? 0,
	});

	const bonusScore = Math.min(Math.max(0, roomData.scoreBreakdown?.bonus ?? 0), 4);
	const rawFinalScore = baseScore + bonusScore;

	//@note: The final BioScore, which is used for display to the user, is capped at 10 for consistency and comparability
	const displayedScore = Math.round(Math.min(rawFinalScore, 10) * 100) / 100;

	return {
		bonusScore,
		rawFinalScore,
		displayedScore,
	};
}
